import $ from 'cash-dom';
import Ajax from '../../components/ajax';
import spinner from '../../components/spinner';

/**
 * Class FormValidator validates form inputs and shows error on validation fail.
 * Usage:
 * ```
 * const formValidator = new FormValidator('#form');
 *
 * // on form submit
 * formValidator.clearErrors();
 * const hasErrors = formValidator.validate({
 *     'first-name': ['required', 'alpha', 'max|255'],
 *     'last-name': ['required', 'alpha', 'max|255'],
 *     'email': ['required', 'email'],
 *     'phone': ['phone'],
 *     'message': ['required', 'max|1000'],
 * });
 * ```
 *
 * Available rules: required, email, phone (check public methods of the current class).
 */
class FormValidator {
  /**
   *
   * @param {HTMLFormElement|Cash} form
   */
  constructor(form) {
    this._$form = $(form);
  }

  /**
   * Clears form's errors.
   */
  clearErrors() {
    this._$form.find('.form-group .error-message').text(null);
    this._$form.find('.form-group').removeClass('error');
  }

  /**
   * Sets form's validation errors. Useful to set errors from server.
   * @param {Object} errors Object in format `{'field-name': {error: 'required', message: 'This field is required'}}`.
   */
  setValidationErrors(errors) {
    for (const [fieldName, fieldErrors] of Object.entries(errors)) {
      const $inputField = this._$form.find(`[name=${fieldName}]`);

      if (!$inputField.length) {
        continue;
      }

      if (typeof fieldErrors === 'string') {
        this._setError($inputField, fieldErrors);
      } else {
        const firstError = fieldErrors[0];

        if (firstError && firstError.message) {
          this._setError($inputField, firstError.message);
        }
      }
    }
  }

  /**
   * Validates form asynchronously.
   * Example: `{'first-name': ['required']}. Available rules: `required, email, phone`.
   * @param {Object} validateOptions Object in format
   * @return {Promise<boolean>} Returns Promise with hasErrros result.
   */
  validateAsync(validateOptions) {
    const {hasErrors, asyncRulesResults} = this._checkAndGetHasErrorsAndAsyncResutls(validateOptions);

    return new Promise((resolve, reject) => {
      if (hasErrors) {
        resolve(hasErrors);
      } else if (asyncRulesResults.length) {
        this._resolveAllAsyncRulesResults(resolve, asyncRulesResults);
      } else {
        // no errors
        resolve(hasErrors);
      }
    });
  }

  _checkAndGetHasErrorsAndAsyncResutls(validateOptions) {
    let hasErrors = false;
    const asyncRulesResults = [];

    let hasErrorsArr = [];
    for (const [fieldName, rules] of Object.entries(validateOptions)) {
      //names can contain [] for multiple field. they should be escaped before using in .find()
      const $inputField = this._$form.find(`[name=${fieldName.replace(/\[/g,'\\[').replace(/\]/g,'\\]')}]`);

      if (!$inputField.length) {
        continue;
      }

      for (const rule of rules) {
        const ruleParts = rule.split('|');
        const ruleMethod = ruleParts[0];

        ruleParts.shift();
        const ruleParams = ruleParts;

        if (!this._isRule(ruleMethod)) {
          continue;
        }

        const ruleResult = this[ruleMethod]($inputField, ...ruleParams);
        let valid = ruleResult;

        if (ruleResult instanceof Promise) {
          asyncRulesResults.push(ruleResult);
        } else {
          hasErrors = !valid;
          hasErrorsArr.push(hasErrors);
        }

        if (hasErrors) {
          break;
        }
      }
    }

    if (hasErrorsArr.includes(true)) {
      hasErrors = true;
    }

    return {hasErrors, asyncRulesResults};
  }

  _isRule(rule) {
    return (typeof this[rule] === 'function');
  }

  _resolveAllAsyncRulesResults(resolve, asyncRulesResults) {
    let hasErrors = false;

    Promise.all(asyncRulesResults).then(rulesResults => {
      for (const isValidRuleResult of rulesResults) {
        hasErrors = !isValidRuleResult;

        if (hasErrors) {
          resolve(hasErrors);
          return;
        }
      }

      // no errors
      resolve(hasErrors);
    });
  }

  /***************** Rules ************************/

  required($inputField) {
    let inputType = $inputField.attr('type'),
        value = null;

    if (inputType === 'radio' || inputType === 'checkbox') {
      let checkedInput = $('[name="' + $inputField.attr('name') + '"]:checked');

      value = checkedInput && checkedInput.length ? checkedInput.val() : null;
    } else {
      value = $inputField.val();
    }

    if (!value) {
      this._setError($inputField, tr('This field is required'));
      return false;
    }

    return true;
  }

  email($inputField) {
    const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return this._validateRegex(regex, $inputField, tr('Please input valid email address'));
  }

  phone($inputField) {
    const regex = /^[0-9\-\(\)\/\+\s]*$/im;
    return this._validateRegex(regex, $inputField, tr('Phone number must contain numbers'));
  }

  alpha($inputField) {
    const regex = /^[A-Za-z |-]+$|^[а-яА-ЯЁёІі |-]+$/;
    return this._validateRegex(regex, $inputField, tr('The field can contain only letters'));
  }

  max($inputField, maxCharacters) {
    const value = $inputField.val();

    if (typeof value === 'string' && value.length > maxCharacters) {
      this._setError(
          $inputField,
          tr('The field can not contain more than') + ' ' + maxCharacters + ' ' + tr('characters')
      );
      return false;
    }

    return true;
  }

  min($inputField, minCharacters) {
    const value = $inputField.val();

    if (typeof value === 'string' && value.length < minCharacters) {
      this._setError(
          $inputField,
          tr('The field must contain more than') + ' ' + minCharacters + ' ' + tr('characters')
      );
      return false;
    }

    return true;
  }

  numeric($inputField) {
    const value = $inputField.val();

    if (/^\d+$/.test(value)) {
      return true;
    } else {
      this._setError(
          $inputField,
          tr('The field must contain more number')
      );
      return false;
    }
  }

  maxNumber($inputField, maxNum) {
    const value = $inputField.val();

    if (/^\d+$/.test(value) && parseFloat(value) > maxNum) {
      this._setError(
          $inputField,
          tr('The field can not contain more than') + ' ' + maxNum
      );
      return false;
    }

    return true;
  }

  minNumber($inputField, minNum) {
    const value = $inputField.val();

    if (/^\d+$/.test(value) && parseFloat(value) < minNum) {
      this._setError(
          $inputField,
          tr('The field can not contain less than') + ' ' + minNum
      );
      return false;
    }

    return true;
  }

  date($inputField) {
    const regex = $inputField.attr('type') === 'month' ? /^[0-9]{4}-[0-9]{2}$/ : /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
    return this._validateRegex(regex, $inputField, tr('Invalid date'));
  }

  dateMax($inputField, dateMax) {
    const dateMaxTime = new Date(dateMax).getTime(),
          inputTime = new Date($inputField.val()).getTime();

    if (inputTime > dateMaxTime) {
      this._setError(
          $inputField,
          tr('Invalid date')
      );
      return false;
    }

    return true;
  }

  dateMin($inputField, dateMin) {
    const dateMinTime = new Date(dateMin).getTime(),
        inputTime = new Date($inputField.val()).getTime();

    if (inputTime < dateMinTime) {
      this._setError(
          $inputField,
          tr('Invalid date')
      );
      return false;
    }

    return true;
  }

  zipcode($inputField) {
    const regex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
    return this._validateRegex(regex, $inputField, tr('Invalid zipcode'));
  }

  zipcodeExists($inputField) {
    const zipcode = $inputField.val();

    return new Promise((resolve, reject) => {
      spinner.show();

      Ajax.post(ajaxurl, {
        action: 'zipcode-exists-check',
        zipcode,
      }, (response) => {
        if (!response.exists) {
          this._setError($inputField, tr('Zipcode does not exist'));
        }

        resolve(response.exists);
      }, 'json').fail((err) => {
        reject(err);
      }).always(() => {
        spinner.hide();
      });
    });
  }

  fullName($inputField) {
    if (!$inputField.val()) {
      return true;
    }

    const value = $inputField.val().trim();
    const parts = value.split(/\s+/);

    if (parts.length < 2) {
      this._setError($inputField, tr('Please enter both first and last name'));
      return false;
    }

    const regex = /^[A-Za-zА-Яа-яЁёІіЇїЄєҐґ'\-]+$/;

    for (let part of parts) {
      if (!regex.test(part)) {
        this._setError($inputField, tr('The name can contain only letters'));
        return false;
      }
    }

    return true;
  }

  usPhone($inputField) {
    const regex = /^[0-9\-\(\)\/\+\s]*$/im;
    let result = this._validateRegex(regex, $inputField, tr('Invalid phone number'));

    if (result) {
      //check US phone rules
      let value = String($inputField.val()).toLowerCase().replace(/\)|\(|-|\+|\s/ig, '');

      if (value.length !== 10 || /^[01]/.test(value)) {
        result = false;
        this._setError($inputField, tr('Invalid phone number'));
      }
    }

    return result;
  }

  _validateRegex(regex, $inputField, message) {
    if (!$inputField.val()) {
      // we don't handle, if not value, it's `required` rule responsibility to be not empty
      return true;
    }

    if (!regex.test(String($inputField.val()).toLowerCase())) {
      this._setError($inputField, message);
      return false;
    }

    return true;
  }

  _setError($inputField, message) {
    $inputField.closest('.form-group').addClass('error').find('.error-message').text(message);
  }
}

export default FormValidator;
